/**
 * Gallery Randomize Queue Store - Pre-randomized artwork selections for instant randomization
 */

import { GALLERY_CONFIG } from '../gallery-config.js';
import { ArrayUtils } from '../utils/array-utils.js';

export function createRandomizeQueueStore() {
  return {
    next_selection: null,
    next_selection_images_preloaded: false,
    queue_preparing: false,

    queue_generation_count: 0,
    last_generation_time: 0,

    isQueueReady() {
      return this.next_selection !== null &&
             this.next_selection_images_preloaded &&
             !this.queue_preparing;
    },

    consumeNextSelection() {
      if (!this.isQueueReady()) {
        console.warn('[RandomizeQueue] Queue not ready, falling back to synchronous randomize');
        return null;
      }

      const selection = this.next_selection;

      this.next_selection = null;
      this.next_selection_images_preloaded = false;

      // Trigger regeneration in background (non-blocking)
      setTimeout(() => {
        this.generateNextRandomSelection();
      }, 50);

      return selection;
    },

    async generateNextRandomSelection() {
      if (this.queue_preparing) {
        return;
      }

      this.queue_preparing = true;
      const start_time = performance.now();

      try {
        const core_store = Alpine.store('galleryCore');
        const products_store = Alpine.store('galleryProducts');
        const memory_store = Alpine.store('galleryMemory');
        const variants_store = Alpine.store('galleryVariants');

        const available = products_store.getCurrentProducts();
        const required_count = core_store.required_artwork_count;

        if (available.length < required_count) {
          console.warn('[RandomizeQueue] Not enough products for queue generation');
          this.queue_preparing = false;
          return;
        }

        const randomized = ArrayUtils.randomizeSelection(available, required_count);

        const image_urls = new Set();
        const { frame, mount, size, locked } = core_store.variant_selection;

        if (!core_store.variant_manager) {
          core_store.initVariantManager();
        }

        randomized.forEach(artwork => {
          if (!artwork) return;

          let variant;
          if (locked && size) {
            variant = core_store.variant_manager?.findVariant(artwork, core_store.variant_selection);
          } else if (frame || mount) {
            variant = core_store.variant_manager?.findVariant(artwork, {
              ...core_store.variant_selection,
              mode: 'preview'
            });
          }

          const image_src = variant?.featured_image?.src || artwork.featured_image?.src;
          if (image_src) {
            const base_url = image_src.split('?')[0];
            image_urls.add(`${base_url}?width=630`);
            image_urls.add(`${base_url}?width=872`);
            image_urls.add(`${base_url}?width=160`);
            image_urls.add(`${base_url}?width=536`);
            image_urls.add(`${base_url}?width=360`);
            image_urls.add(`${base_url}?width=180`);
            image_urls.add(`${base_url}?width=300`);
          }
        });

        const urls_array = Array.from(image_urls);
        const uncached = urls_array.filter(url =>
          !memory_store.isImagePreloaded(url, core_store.orientation)
        );

        if (uncached.length > 0) {
          memory_store.incrementImageLoads(uncached.length);
          await memory_store.preloadImageBatch(uncached, core_store.orientation);
        }

        this.next_selection = randomized;
        this.next_selection_images_preloaded = true;

        this.queue_generation_count++;
        this.last_generation_time = performance.now() - start_time;

      } catch (error) {
        console.error('[RandomizeQueue] Failed to generate queue:', error);
        this.next_selection = null;
        this.next_selection_images_preloaded = false;
      } finally {
        this.queue_preparing = false;
      }
    },

    clearQueue() {
      this.next_selection = null;
      this.next_selection_images_preloaded = false;
      this.queue_preparing = false;
    },

    async regenerateQueue() {
      this.clearQueue();
      await this.generateNextRandomSelection();
    },

    getQueueStats() {
      return {
        ready: this.isQueueReady(),
        preparing: this.queue_preparing,
        generation_count: this.queue_generation_count,
        last_generation_time: `${Math.round(this.last_generation_time)}ms`,
        queue_size: this.next_selection?.length || 0
      };
    }
  };
}
